/* EDU-117 — espace chef d'établissement (rôle direction). Classes, élèves, */
/* enseignants et associations (prof, matière, classe). RLS scope l'écriture à */
/* la direction de l'école. */

import { api } from './api';

export interface Classroom { id: string; school_id: string; name: string; level: string | null; level_id: string; archived: boolean; school_year?: string; created_at: string }
export interface Level { id: string; code: string; label: string; position: number }
export interface Subject { id: string; code: string; label: string; nature: string; position: number }

/** Référentiel des niveaux (public.levels), ordonné. */
/* CLEAN-ARCH (Vague 4) : classes/levels/école via les ports tRPC direction */
/* (école dérivée + scopée server-side). Le reste (élèves/profs) au fil de l'eau. */
export async function listLevels(): Promise<Level[]> {
  return (await api.direction.levels.query()) as Level[];
}
/** Référentiel des matières (public.subjects), ordonné. */
export async function listSubjects(): Promise<Subject[]> {
  return (await api.direction.subjects.query()) as Subject[];
}
export interface Student { id: string; classroom_id: string; school_id: string; first_name: string; last_name: string; email: string | null; active: boolean; created_at: string; code: string; notes: string | null }
export interface SchoolTeacher {
  id: string;
  school_id: string;
  user_id?: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  active: boolean;
  created_at: string;
  invite_status?: 'assigned' | 'invited' | 'none';
}
export interface ClassroomTeacher {
  id: string;
  classroom_id: string;
  school_id: string;
  school_teacher_id: string;
  subject_id: string | null;
  subject_label: string | null;
  created_at: string;
}

/** The direction user's school (first 'direction' school assignment). */
export async function getMySchoolId(): Promise<string | null> {
  return api.direction.mySchoolId.query();
}

/* ---- Classes --------------------------------------------------------------- */
export async function listClassrooms(_schoolId: string): Promise<Classroom[]> {
  /* École dérivée server-side ; le paramètre est conservé pour la signature. */
  return (await api.direction.classrooms.list.query()) as Classroom[];
}
export async function createClassroom(_schoolId: string, name: string, levelId: string): Promise<Classroom> {
  return (await api.direction.classrooms.create.mutate({ name, levelId })) as Classroom;
}
export async function updateClassroom(id: string, patch: { name?: string; level_id?: string; archived?: boolean }): Promise<void> {
  await api.direction.classrooms.update.mutate({ id, patch });
}

/* ---- Élèves ---------------------------------------------------------------- */
/* CLEAN-ARCH (Vague 4) : élèves via les ports tRPC direction (école scopée server-side). */
export async function listStudents(classroomId: string): Promise<Student[]> {
  return (await api.direction.students.list.query({ classroomId })) as Student[];
}
export async function listSchoolStudents(_schoolId: string): Promise<Student[]> {
  return (await api.direction.students.listSchool.query()) as Student[];
}
export interface SchoolStudentsPage {
  students: Student[];
  total: number;
}
export async function listSchoolStudentsPaginated(page: number, limit: number, search?: string): Promise<SchoolStudentsPage> {
  return (await api.direction.students.listSchoolPaginated.query({ page, limit, search })) as SchoolStudentsPage;
}
export async function addStudent(input: { classroomId: string; schoolId: string; firstName: string; lastName: string; email?: string }): Promise<Student> {
  return (await api.direction.students.add.mutate({ classroomId: input.classroomId, firstName: input.firstName, lastName: input.lastName, email: input.email })) as Student;
}
export async function moveStudent(id: string, classroomId: string): Promise<void> {
  await api.direction.students.move.mutate({ id, classroomId });
}
export interface MoveStudentsResult { movedIds: string[]; failed: { id: string; error: string }[] }
export async function moveStudents(ids: string[], classroomId: string): Promise<MoveStudentsResult> {
  return (await api.direction.students.moveMany.mutate({ ids, classroomId })) as MoveStudentsResult;
}
export async function renameStudent(id: string, firstName: string, lastName: string): Promise<void> {
  await api.direction.students.rename.mutate({ id, firstName, lastName });
}
export async function updateStudentNotes(id: string, notes: string | null): Promise<void> {
  await api.direction.students.updateNotes.mutate({ id, notes });
}
export async function setStudentActive(id: string, active: boolean): Promise<void> {
  await api.direction.students.setActive.mutate({ id, active });
}
export async function deleteStudent(id: string): Promise<void> {
  await api.direction.students.delete.mutate({ id });
}

export async function inviteParentOrTutor(email: string, studentIds: string[]): Promise<void> {
  await api.direction.parents.invite.mutate({ email, studentIds });
}

export interface ParentInvitation {
  id: string;
  email: string;
  status: string;
  created_at: string;
  student_ids: string[];
}
export async function listParentInvitations(): Promise<ParentInvitation[]> {
  return (await api.direction.parents.list.query()) as ParentInvitation[];
}
export async function resendParentInvite(invitationId: string): Promise<void> {
  await api.direction.parents.resend.mutate({ invitationId });
}

/* ---- Enseignants ----------------------------------------------------------- */
/* CLEAN-ARCH (Vague 4) : enseignants + assignations via les ports tRPC direction. */
export async function listTeachers(_schoolId: string): Promise<SchoolTeacher[]> {
  return (await api.direction.teachers.list.query()) as SchoolTeacher[];
}
export async function addTeacher(input: { schoolId: string; firstName: string; lastName: string; email?: string }): Promise<SchoolTeacher> {
  return (await api.direction.teachers.add.mutate({ firstName: input.firstName, lastName: input.lastName, email: input.email })) as SchoolTeacher;
}
export async function updateTeacher(id: string, patch: { first_name?: string; last_name?: string; email?: string | null; active?: boolean }): Promise<void> {
  await api.direction.teachers.update.mutate({ id, patch });
}
export async function deleteTeacher(id: string): Promise<void> {
  await api.direction.teachers.delete.mutate({ id });
}
export async function resendTeacherInvite(id: string): Promise<'invited' | 'assigned'> {
  return api.direction.teachers.resendInvite.mutate({ id });
}

/* ---- Groupes de classes (bloc 10b) ----------------------------------------- */
/* Regroupements nommés de classes, base de la délégation direction. Gestion */
/* réservée au directeur école (dérivée server-side de son rôle scopé école). */
export interface ClassGroupClassroomRef { id: string; name: string; level: string | null }
export interface ClassGroup { id: string; school_id: string; name: string; created_at: string; classrooms: ClassGroupClassroomRef[] }

export async function listClassGroups(): Promise<ClassGroup[]> {
  return (await api.direction.groups.list.query()) as ClassGroup[];
}
export async function createClassGroup(name: string): Promise<{ id: string; school_id: string; name: string; created_at: string }> {
  return api.direction.groups.create.mutate({ name });
}
export async function renameClassGroup(id: string, name: string): Promise<void> {
  await api.direction.groups.rename.mutate({ id, name });
}
export async function setClassGroupClassrooms(id: string, classroomIds: string[]): Promise<void> {
  await api.direction.groups.setClassrooms.mutate({ id, classroomIds });
}
export async function deleteClassGroup(id: string): Promise<void> {
  await api.direction.groups.delete.mutate({ id });
}

/* ---- Équipe de direction / délégation (bloc 10b lot 2) --------------------- */
export interface DirectionCapabilities { is_director: boolean; is_delegate: boolean }
export interface DirectionDelegateGroup { access_id: string; group_id: string; group_name: string }
export interface DirectionDelegate {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  groups: DirectionDelegateGroup[];
}

export async function getDirectionCapabilities(): Promise<DirectionCapabilities> {
  return api.direction.capabilities.query();
}
export async function listDirectionTeam(): Promise<DirectionDelegate[]> {
  return (await api.direction.team.list.query()) as DirectionDelegate[];
}
export async function inviteDelegate(email: string, groupIds: string[]): Promise<{ status: 'invited' | 'assigned'; email: string }> {
  return api.direction.team.invite.mutate({ email, groupIds });
}
export async function revokeGroupAccess(accessId: string): Promise<void> {
  await api.direction.team.revoke.mutate({ accessId });
}

/* ---- Mes classes (bloc 10b lot 3) — périmètre directeur ou adjoint ---------- */

export interface ClassroomSummary extends Classroom {
  active_count: number;
  total_count: number;
  primary_teacher_name: string | null;
}

export interface ClassroomsPage {
  classrooms: ClassroomSummary[];
  total: number;
}

export interface StudentsPage {
  students: Student[];
  total: number;
}

export interface ClassroomTeacherName {
  school_teacher_id: string;
  first_name: string;
  last_name: string;
  subject_label: string | null;
}

export interface ClassroomDetail extends Classroom {
  active_count: number;
  total_count: number;
  teachers: ClassroomTeacherName[];
}

export async function listMyClassrooms(): Promise<Classroom[]> {
  return (await api.direction.member.classrooms.query()) as Classroom[];
}

export async function listMyClassroomsPaginated(page: number, limit: number): Promise<ClassroomsPage> {
  return (await api.direction.member.classroomsPaginated.query({ page, limit })) as ClassroomsPage;
}

export async function getMyClassroomDetail(classroomId: string): Promise<ClassroomDetail | null> {
  return (await api.direction.member.classroomDetail.query({ classroomId })) as ClassroomDetail | null;
}

export async function listMyClassroomStudents(classroomId: string): Promise<Student[]> {
  return (await api.direction.member.students.list.query({ classroomId })) as Student[];
}

export async function listMyClassroomStudentsPaginated(classroomId: string, page: number, limit: number): Promise<StudentsPage> {
  return (await api.direction.member.students.paginated.query({ classroomId, page, limit })) as StudentsPage;
}
export async function addMyStudent(input: { classroomId: string; firstName: string; lastName: string; email?: string }): Promise<Student> {
  return (await api.direction.member.students.add.mutate(input)) as Student;
}
export async function setMyStudentActive(id: string, active: boolean): Promise<void> {
  await api.direction.member.students.setActive.mutate({ id, active });
}
export async function moveMyStudent(id: string, classroomId: string): Promise<void> {
  await api.direction.member.students.move.mutate({ id, classroomId });
}
export async function inviteMyStudentParent(email: string, studentIds: string[]): Promise<void> {
  await api.direction.member.inviteParent.mutate({ email, studentIds });
}

/* ---- Onboarding directeur -------------------------------------------------- */
export type FrLegalStatus = 'public' | 'prive_contrat' | 'prive_simple' | 'hors_contrat';

export interface DirectionStatus {
  needs_onboarding: boolean;
  school_id: string;
  school_name: string;
}

export interface CompleteOnboardingPayload {
  school: {
    name: string;
    address?: string | null;
    fr_legal_status: FrLegalStatus;
    school_year?: string | null;
    student_count?: number | null;
  };
  profile: {
    first_name: string;
    last_name: string;
    poste?: string | null;
  };
  second_holder?: {
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

export async function getDirectionStatus(): Promise<DirectionStatus | null> {
  return api.direction.status.query();
}

export async function completeOnboarding(input: CompleteOnboardingPayload): Promise<void> {
  await api.direction.completeOnboarding.mutate(input);
}

/* ---- Contenus (bloc 10c) — lecture seule, tous profs de l'établissement ---- */
export type SchoolContentDocumentKind = 'general' | 'course' | 'exercice_sheet' | 'exam';

export interface SchoolContentDocument {
  id: string;
  teacher_id: string;
  teacher_name: string;
  school_id: string;
  kind: SchoolContentDocumentKind;
  title: string;
  level_id: string;
  subject_id: string;
  folder_id: string | null;
  global_correction_rules: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SchoolContentDocumentsPage {
  items: SchoolContentDocument[];
  total: number;
}

export interface SchoolContentDocumentSection {
  id: string;
  document_id: string;
  position: number;
  type: string;
  title: string | null;
  content: unknown;
  created_at: string;
  updated_at: string;
}

/* Le détail n'expose pas teacher_name (getWithSectionsForSchool renvoie le
   document brut, sans jointure) — la page le résout via la liste des profs déjà
   chargée pour les filtres. */
export interface SchoolContentDocumentDetail {
  id: string;
  teacher_id: string;
  school_id: string;
  kind: SchoolContentDocumentKind;
  title: string;
  level_id: string;
  subject_id: string;
  folder_id: string | null;
  global_correction_rules: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  sections: SchoolContentDocumentSection[];
}

export interface ListSchoolContentDocumentsFilters {
  page?: number;
  limit?: number;
  teacherId?: string;
  kind?: SchoolContentDocumentKind;
  levelId?: string;
  subjectId?: string;
  search?: string;
  includeArchived?: boolean;
  includeTestData?: boolean;
}

export async function listSchoolContentDocuments(
  filters: ListSchoolContentDocumentsFilters = {},
): Promise<SchoolContentDocumentsPage> {
  return (await api.direction.content.list.query(filters)) as SchoolContentDocumentsPage;
}

export async function getSchoolContentDocument(id: string): Promise<SchoolContentDocumentDetail | null> {
  return (await api.direction.content.get.query({ id })) as SchoolContentDocumentDetail | null;
}

/* ---- Associations (prof, matière, classe) ---------------------------------- */
export async function listClassroomTeachers(classroomId: string): Promise<ClassroomTeacher[]> {
  return (await api.direction.assignments.list.query({ classroomId })) as ClassroomTeacher[];
}
export async function assignTeacher(input: { classroomId: string; schoolId: string; teacherId: string; subjectId: string }): Promise<void> {
  await api.direction.assignments.assign.mutate({ classroomId: input.classroomId, teacherId: input.teacherId, subjectId: input.subjectId });
}
export async function unassignTeacher(id: string): Promise<void> {
  await api.direction.assignments.unassign.mutate({ id });
}
